import { CustomHomepageGrid } from '@backstage/plugin-home';
import { datadogRum } from '@datadog/browser-rum';
import { FavoriteLinksWidget } from './widgets/favoriteLinks/FavoriteLinks';
import { HeaderHomePage } from './header/Header';
import { NewsAndUpdatesWidget } from './widgets/newsAndUpdates/NewsAndUpdates';
import { OncallWidget } from './widgets/onCall/OnCall';
import { OngoingIncidentsWidget } from './widgets/ongoingIncidents/OngoingIncidents';
import { ScoreCardsWidget } from './widgets/scoreCards/ScoreCards';
import { useStyles } from './homePage.style';
import { XkcdComicCard } from 'backstage-plugin-xkcd';

import { Content, Page } from '@backstage/core-components';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export const HomePage = () => {
  const { cx, classes } = useStyles();
  const [showHeader, setShowHeader] = useState(true);
  const [buttonWidth, setButtonWidth] = useState(0);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const homePageGridRef = useRef<HTMLDivElement>(null);

  const defaultConfig = useMemo(
    () => [
      {
        component: 'Scorecards',
        x: 0,
        y: 0,
        width: 6,
        height: 5,
        deletable: false,
      },
      {
        component: 'News and updates',
        x: 6,
        y: 0,
        width: 6,
        height: 5,
        deletable: false,
      },
    ],
    [],
  );

  const updateUI = useCallback(() => {
    const buttons = Array.from(document.querySelectorAll('button'));
    const editButton = buttons.find(
      btn => btn.textContent?.trim().toLowerCase() === 'edit',
    );
    const saveButton = buttons.find(
      btn => btn.textContent?.trim().toLowerCase() === 'save',
    );
    const onCallEditModeButtons = buttons.filter(btn =>
      btn.classList.contains('go-to-edit-mode'),
    );

    const isSaveMode = buttons.some(
      btn => btn.textContent?.trim().toLowerCase() === 'save',
    );

    // track intention homepage changes (widget/ layout changes etc.)
    if (saveButton) {
      if (!saveButton.dataset.hasClickListener) {
        saveButton.addEventListener('click', function handleSaveButtonClick() {
          datadogRum.addAction('homepage_change_intention');
        });
      }
      saveButton.dataset.hasClickListener = 'true';
    }

    // on call widget modifications
    if (editButton) {
      onCallEditModeButtons.forEach(button => {
        button.addEventListener(
          'click',
          function handleOnCallChooseTeamClick() {
            editButton.click();
          },
        );
      });
    }

    // settings of widgets process modifications
    setButtonWidth(editButton?.offsetWidth || 0);
    setShowHeader(!isSaveMode);

    if (homePageGridRef.current) {
      homePageGridRef.current.classList.toggle(
        classes.homePageGridSaveMode,
        isSaveMode,
      );
    }
  }, [classes.homePageGridSaveMode]);

  const hideClearAllButton = useCallback(() => {
    const clearAllButton = Array.from(document.querySelectorAll('button')).find(
      button => button.textContent?.trim().toLowerCase() === 'clear all',
    );

    if (clearAllButton) {
      clearAllButton.classList.add(classes.hidden);
    }
  }, [classes.hidden]);

  useEffect(() => {
    updateUI();
    hideClearAllButton();
    const observer = new MutationObserver(() => {
      updateUI();
      hideClearAllButton();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [updateUI, hideClearAllButton]);

  useLayoutEffect(() => {
    if (headerContainerRef.current) {
      const header = headerContainerRef.current;
      header.classList.add(classes.header);
      header.style.setProperty('--button-width', `${buttonWidth}px`);
    }
  }, [buttonWidth, showHeader, classes.header]);

  return (
    <Page themeId="home">
      <Content>
        <div className={cx(classes.homePage)}>
          {showHeader && (
            <div ref={headerContainerRef}>
              <HeaderHomePage />
            </div>
          )}
          <div ref={homePageGridRef} className={classes.homePageGrid}>
            <CustomHomepageGrid config={defaultConfig}>
              <ScoreCardsWidget />
              <XkcdComicCard />
              <NewsAndUpdatesWidget />
              <OncallWidget />
              <OngoingIncidentsWidget />
              <FavoriteLinksWidget />
            </CustomHomepageGrid>
          </div>
        </div>
      </Content>
    </Page>
  );
};
